import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Button, SubmitButton, Typography } from "@avenue-8/sidekick-ui";
import { css } from "@mui/material";
import type { useUpdateContact } from "@shared/generated/crm";
import {
  ContactFieldItem,
  countPendingSuggestionsAgainstValues,
  toUpdateContactBody,
  updateContactBodySchema,
} from "./contact-fields-helpers";
import { ContactFieldsFormField } from "./ContactFieldsFormField";
import { validateSocialHandleFields } from "./contact-summary-chips-helpers";

type ContactFieldsFormProps = {
  contactId: string;
  latestPendingFieldSignalAt: string | undefined;
  contactFields: ContactFieldItem[];
  isEditing: boolean;
  useSuggestedValues: boolean;
  updateContact: ReturnType<typeof useUpdateContact>["mutate"];
  isSavingContact: boolean;
  isSavingContactError: boolean;
  onReviewSuggestions: () => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onPendingSuggestionsCountChange?: (count: number) => void;
};

type ContactFieldsFormValues = Record<string, string>;

const toFormValues = (
  contactFields: ContactFieldItem[],
  useSuggestedValues: boolean,
): ContactFieldsFormValues =>
  Object.fromEntries(
    contactFields.map((field) => [
      field.id,
      useSuggestedValues ? (field.suggestedValue ?? field.value) : field.value,
    ]),
  );

export const ContactFieldsForm = (props: ContactFieldsFormProps) => {
  const {
    contactId,
    latestPendingFieldSignalAt,
    contactFields,
    isEditing,
    useSuggestedValues = false,
    updateContact,
    isSavingContact,
    isSavingContactError,
    onReviewSuggestions,
    onStartEditing,
    onCancelEditing,
    onPendingSuggestionsCountChange,
  } = props;
  const formDefaultValues = useMemo(
    () => toFormValues(contactFields, useSuggestedValues),
    [contactFields, useSuggestedValues],
  );

  const form = useForm<ContactFieldsFormValues>({
    defaultValues: formDefaultValues,
  });
  const {
    getValues: getFormValues,
    reset: resetForm,
    setValue: setFormValue,
    watch: watchForm,
    setError: setFormError,
    clearErrors: clearFormErrors,
  } = form;

  const watchedFormValues = watchForm();
  const pendingSuggestionsCount = useMemo(
    () =>
      countPendingSuggestionsAgainstValues(contactFields, watchedFormValues),
    [contactFields, watchedFormValues],
  );
  const hasPendingSuggestions =
    pendingSuggestionsCount > 0 && !useSuggestedValues;
  const pendingSuggestionsCopy = `There are ${pendingSuggestionsCount} pending AI suggestions to be reviewed.`;

  useEffect(() => {
    resetForm(formDefaultValues);
  }, [resetForm, formDefaultValues]);

  const notifyPendingSuggestionsCount = useCallback(
    (nextValues: ContactFieldsFormValues) => {
      onPendingSuggestionsCountChange?.(
        countPendingSuggestionsAgainstValues(contactFields, nextValues),
      );
    },
    [contactFields, onPendingSuggestionsCountChange],
  );

  const handleDismissSuggestedValue = (fieldId: string) => {
    const nextValue = contactFields.find((f) => f.id === fieldId)?.value ?? "";
    setFormValue(fieldId, nextValue);
    notifyPendingSuggestionsCount({
      ...getFormValues(),
      [fieldId]: nextValue,
    });
  };

  const handleSeeSuggestedValue = (fieldId: string) => {
    const nextValue =
      contactFields.find((f) => f.id === fieldId)?.suggestedValue ?? "";
    setFormValue(fieldId, nextValue);
    notifyPendingSuggestionsCount({
      ...getFormValues(),
      [fieldId]: nextValue,
    });
  };

  const handleSubmit = () => {
    clearFormErrors();
    const socialHandleErrors = validateSocialHandleFields(getFormValues());
    const socialHandleErrorEntries = Object.entries(socialHandleErrors);
    if (socialHandleErrorEntries.length > 0) {
      socialHandleErrorEntries.forEach(([fieldId, message]) => {
        setFormError(fieldId, { type: "social-handle", message });
      });
      return;
    }

    const contactUpdateDataParsed = updateContactBodySchema.safeParse(
      toUpdateContactBody(getFormValues(), latestPendingFieldSignalAt),
    );
    if (!contactUpdateDataParsed.success) {
      contactUpdateDataParsed.error.issues.forEach((issue) => {
        const [firstPathPart] = issue.path;
        if (typeof firstPathPart !== "string") return;
        if (firstPathPart === "latestFieldSignalAt") return;
        setFormError(firstPathPart, {
          type: "zod",
          message: issue.message,
        });
      });
      return;
    }

    updateContact({
      contactId,
      data: contactUpdateDataParsed.data,
    });
  };

  const handleCancelChanges = () => {
    const resetValues = toFormValues(contactFields, false);
    resetForm(resetValues);
    notifyPendingSuggestionsCount(resetValues);
    onCancelEditing();
  };

  const handleFormChange = useCallback(
    (
      fieldId: string,
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      clearFormErrors(fieldId);
      notifyPendingSuggestionsCount({
        ...getFormValues(),
        [fieldId]: event.target.value,
      });
    },
    [clearFormErrors, getFormValues, notifyPendingSuggestionsCount],
  );

  return (
    <FormProvider {...form}>
      {hasPendingSuggestions && (
        <PendingSuggestionsBanner>
          <BannerText>{pendingSuggestionsCopy}</BannerText>
          <Button
            color="secondary"
            size="small"
            variant="outlined"
            onClick={onReviewSuggestions}
          >
            Review
          </Button>
        </PendingSuggestionsBanner>
      )}
      <ContactFieldGrid $isEditing={isEditing}>
        {contactFields.map((field) => {
          return (
            <ContactFieldsFormField
              key={field.id}
              field={field}
              isEditing={isEditing}
              onChange={handleFormChange}
              onDismissSuggestedValue={handleDismissSuggestedValue}
              onSeeSuggestedValue={handleSeeSuggestedValue}
            />
          );
        })}
      </ContactFieldGrid>
      {isSavingContactError && (
        <Typography color="error" variant="body2">
          Something went wrong saving these changes. Please try again.
        </Typography>
      )}
      <FormActions>
        {!isEditing && (
          <Button
            color="secondary"
            size="small"
            variant="outlined"
            onClick={onStartEditing}
          >
            Edit fields
          </Button>
        )}
        {isEditing && (
          <>
            <Button
              color="secondary"
              size="small"
              variant="outlined"
              onClick={handleCancelChanges}
              disabled={isSavingContact}
            >
              Cancel
            </Button>
            <SubmitButton
              color="secondary"
              size="small"
              variant="contained"
              onClick={handleSubmit}
              isLoading={isSavingContact}
              loadingText="Saving..."
            >
              Save
            </SubmitButton>
          </>
        )}
      </FormActions>
    </FormProvider>
  );
};

const ContactFieldGrid = styled.div<{ $isEditing?: boolean }>`
  display: grid;
  grid-template-columns: 150px 1fr;
  text-align: left;
  justify-content: center;
  transition: grid-template-columns 0.2s ease;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }
  ${(p) =>
    p.$isEditing &&
    css`
      grid-template-columns: 0.5fr 1fr 20px 1fr;
      ${p.theme.media.mobileDown} {
        grid-template-columns: 1fr;
      }
    `}
`;

const PendingSuggestionsBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: solid 1px ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  margin-bottom: 0.75rem;

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BannerText = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.s};
`;

const FormActions = styled.div`
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;
